import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Loader2, Search, Gift, Trash2, ShieldAlert, Clock, Sparkles, Wand2, CalendarClock, Crown } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import { toast } from 'sonner';

/**
 * Admin — Feature Grants (Jun 2026)
 *
 * Support-facing UI to hand out or revoke grants on the three AI
 * surfaces. Wraps `/api/admin/grants` (list/create/delete). Meant to
 * live at `/admin/grants` — accessible only to admins.
 *
 * Grants replace-on-conflict for `(user_id, feature)`, so hitting the
 * button twice EXTENDS the trial instead of stacking two rows.
 */

const FEATURES = [
  { slug: 'ai_dm',            label: 'AI DM Assistant',      icon: Wand2,         accent: '#2563EB' },
  { slug: 'ai_safety',        label: 'AI Safety Assistant',  icon: Sparkles,      accent: '#7C3AED' },
  { slug: 'ai_date_planner',  label: 'AI Date Planner',      icon: CalendarClock, accent: '#B38138' },
];

const DURATIONS = [
  { key: '24h', label: '24 hours' },
  { key: '3d',  label: '3 days' },
  { key: '7d',  label: '7 days' },
  { key: '30d', label: '30 days' },
];

function relativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const ms = d - now;
  if (Math.abs(ms) < 60_000) return 'now';
  const abs = Math.abs(ms);
  const mins = Math.round(abs / 60_000);
  const hours = Math.round(abs / 3_600_000);
  const days = Math.round(abs / 86_400_000);
  const suffix = ms > 0 ? 'from now' : 'ago';
  if (mins < 60) return `${mins}m ${suffix}`;
  if (hours < 48) return `${hours}h ${suffix}`;
  return `${days}d ${suffix}`;
}

function FeaturePill({ slug }) {
  const f = FEATURES.find((x) => x.slug === slug);
  if (!f) return <span className="text-xs text-[#8A8A93]">{slug}</span>;
  const Icon = f.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-[700]"
      style={{ background: `${f.accent}14`, color: f.accent }}
    >
      <Icon size={11} /> {f.label}
    </span>
  );
}

export default function AdminGrants() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [email, setEmail] = useState('');
  const [feature, setFeature] = useState('ai_dm');
  const [durationKey, setDurationKey] = useState('24h');
  const [reason, setReason] = useState('');
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [includeExpired, setIncludeExpired] = useState(false);

  useEffect(() => {
    api.get('/admin/me')
      .then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const load = useCallback(async (targetEmail) => {
    setLoading(true);
    try {
      const params = { include_expired: includeExpired, limit: 100 };
      if (targetEmail) params.email = targetEmail;
      const r = await api.get('/admin/grants', { params });
      setGrants(r.data?.grants || []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setGrants([]);
        toast.error('No user found for that email');
      } else {
        toast.error('Failed to load grants');
      }
    } finally { setLoading(false); }
  }, [includeExpired]);

  useEffect(() => { if (authChecked) load(); }, [authChecked, load]);

  const search = (e) => { e?.preventDefault(); load(email.trim()); };

  const createGrant = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Enter a user email'); return; }
    setCreating(true);
    try {
      const r = await api.post('/admin/grants', {
        user_email:   email.trim(),
        feature,
        duration_key: durationKey,
        reason:       reason.trim() || null,
      });
      toast.success(`Granted ${feature} for ${r.data?.hours}h`);
      setReason('');
      await load(email.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Grant failed');
    } finally { setCreating(false); }
  };

  const revoke = async (g) => {
    if (!window.confirm(`Revoke ${g.feature} for ${g.user_email || g.user_id}?`)) return;
    try {
      await api.delete('/admin/grants', {
        params: { email: g.user_email || '', feature: g.feature },
      });
      toast.success('Grant revoked');
      await load(email.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFB] pb-24">
      <div className="max-w-3xl mx-auto p-4">
        <BackNavButton to="/admin/support" />
        <header className="mb-6 flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center text-white"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #2563EB)' }}
          >
            <Gift size={20} />
          </div>
          <div>
            <h1 className="font-display-tight text-2xl font-[800] tracking-[-0.4px]">Feature Grants</h1>
            <p className="text-[13px] text-[#6B7280] mt-0.5">
              Comp a single AI surface (DM, Safety, Date Planner) without changing a user&rsquo;s tier.
            </p>
          </div>
        </header>

        {/* ================= Create form ================= */}
        <form
          onSubmit={createGrant}
          data-testid="admin-grants-create-form"
          className="rounded-2xl bg-white p-4 mb-6"
          style={{ border: '1px solid rgba(15,15,16,0.06)' }}
        >
          <p className="text-[10.5px] font-[900] uppercase tracking-[0.14em] text-[#8A8A93] mb-3">Grant access</p>

          <label className="block text-[11px] font-[700] uppercase tracking-[0.1em] text-[#6B7280] mb-1.5">User email</label>
          <div className="flex gap-2 mb-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              data-testid="admin-grants-email"
              className="flex-1 px-3 py-2 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
              style={{ border: '1px solid rgba(15,15,16,0.12)' }}
            />
            <button
              type="button"
              onClick={search}
              data-testid="admin-grants-search"
              className="px-3 py-2 rounded-xl bg-[#0F0F10] text-white text-[13px] font-[700] flex items-center gap-1"
            >
              <Search size={14}/> Look up
            </button>
          </div>

          <label className="block text-[11px] font-[700] uppercase tracking-[0.1em] text-[#6B7280] mb-1.5">Feature</label>
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="admin-grants-feature-picker">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              const selected = f.slug === feature;
              return (
                <button
                  key={f.slug}
                  type="button"
                  onClick={() => setFeature(f.slug)}
                  data-testid={`admin-grants-feature-${f.slug}`}
                  className={`rounded-xl p-2.5 text-[12px] font-[700] flex flex-col items-center gap-1 transition-all ${
                    selected ? 'text-white' : 'text-[#4B4B52] hover:text-[#0F0F10]'
                  }`}
                  style={{
                    background: selected ? f.accent : '#FFFFFF',
                    border: `1px solid ${selected ? f.accent : 'rgba(15,15,16,0.1)'}`,
                  }}
                >
                  <Icon size={16} />
                  <span className="leading-tight text-center">{f.label}</span>
                </button>
              );
            })}
          </div>

          <label className="block text-[11px] font-[700] uppercase tracking-[0.1em] text-[#6B7280] mb-1.5">Duration</label>
          <div className="grid grid-cols-4 gap-2 mb-3" data-testid="admin-grants-duration-picker">
            {DURATIONS.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => setDurationKey(d.key)}
                data-testid={`admin-grants-duration-${d.key}`}
                className={`rounded-xl py-2 text-[12px] font-[700] transition-all ${
                  durationKey === d.key
                    ? 'bg-[#0F0F10] text-white'
                    : 'bg-white text-[#4B4B52] hover:text-[#0F0F10]'
                }`}
                style={{ border: `1px solid ${durationKey === d.key ? '#0F0F10' : 'rgba(15,15,16,0.1)'}` }}
              >
                {d.label}
              </button>
            ))}
          </div>

          <label className="block text-[11px] font-[700] uppercase tracking-[0.1em] text-[#6B7280] mb-1.5">Reason (optional)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Retention save / support comp / promo…"
            data-testid="admin-grants-reason"
            className="w-full px-3 py-2 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 mb-4"
            style={{ border: '1px solid rgba(15,15,16,0.12)' }}
          />

          <button
            type="submit"
            disabled={creating || !email.trim()}
            data-testid="admin-grants-submit"
            className="w-full py-3 rounded-full text-white text-[14px] font-[800] tracking-[-0.2px] disabled:opacity-50 transition-transform hover:scale-[1.01] active:scale-[0.99]"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #2563EB)' }}
          >
            {creating ? <Loader2 className="inline w-4 h-4 animate-spin"/> : 'Create grant'}
          </button>

          <p className="text-[11px] text-[#8A8A93] mt-3 flex items-start gap-1.5">
            <ShieldAlert size={11} className="mt-0.5 flex-shrink-0"/>
            Existing grants for the same feature are extended, not stacked. VIP users already have access — grants are additive for free / premium tiers.
          </p>
        </form>

        {/* ================= Grants list ================= */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[13px] font-[900] uppercase tracking-[0.14em] text-[#4B4B52]">
            {email ? `Grants for ${email}` : 'Recent grants'}
          </h2>
          <label className="flex items-center gap-1.5 text-[12px] text-[#6B7280] cursor-pointer">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(e) => setIncludeExpired(e.target.checked)}
              data-testid="admin-grants-include-expired"
              className="accent-[#7C3AED]"
            />
            Include expired
          </label>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#7C3AED]"/>
          </div>
        ) : grants.length === 0 ? (
          <div
            className="rounded-2xl bg-white p-6 text-center text-[#8A8A93] text-[13px]"
            style={{ border: '1px solid rgba(15,15,16,0.06)' }}
            data-testid="admin-grants-empty"
          >
            No grants yet. Create one above.
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-grants-list">
            {grants.map((g) => (
              <div
                key={g.grant_id || `${g.user_id}-${g.feature}-${g.granted_at}`}
                className="rounded-2xl bg-white p-4 flex items-start gap-3"
                style={{ border: '1px solid rgba(15,15,16,0.06)' }}
                data-testid={`admin-grant-${g.grant_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <FeaturePill slug={g.feature} />
                    {g.is_active ? (
                      <span className="text-[10.5px] font-[800] px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534] uppercase tracking-wider">
                        Active
                      </span>
                    ) : (
                      <span className="text-[10.5px] font-[800] px-1.5 py-0.5 rounded bg-black/[0.06] text-[#6B7280] uppercase tracking-wider">
                        Expired
                      </span>
                    )}
                  </div>
                  <p className="text-[13.5px] font-[700] text-[#0F0F10] truncate">
                    {g.user_email || g.user_id}
                    {g.user_name ? <span className="text-[#8A8A93] font-[500]"> · {g.user_name}</span> : null}
                  </p>
                  <div className="text-[12px] text-[#6B7280] mt-1 flex items-center gap-2 flex-wrap">
                    <Clock size={11} />
                    <span>Expires {relativeTime(g.expires_at)}</span>
                    <span>·</span>
                    <span>by {g.granted_by || 'admin'}</span>
                    {g.reason && <span className="italic text-[#8A8A93]">— {g.reason}</span>}
                  </div>
                </div>
                {g.is_active && (
                  <button
                    onClick={() => revoke(g)}
                    data-testid={`admin-grant-revoke-${g.grant_id}`}
                    aria-label="Revoke grant"
                    className="w-9 h-9 rounded-full flex items-center justify-center text-[#B91C1C] hover:bg-[#FEF2F2] transition-colors flex-shrink-0"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Re-export the crown icon indirectly used above so tree-shaking doesn't
// yell (kept for future badge additions).
export { Crown };
